import { Inject, Injectable } from '@angular/core';
import { lowerFirst } from 'lodash';
import { SETTINGS, ISettings } from '@lsq-frontend/angular-shared/settings';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { Observable, ReplaySubject } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { LocalStorageKeys } from '../../constants/local-storage-keys.constants';
import { IClaims } from '../../models/common';
import { WindowReferenceService } from '../../services/window-reference/window-reference.service';

/**
 * Service for managing claims.
 */
@Injectable({ providedIn: 'root' })
export class ClaimService {
    private _hasLocalClaims = false;
    private _application: string;
    private _claimSubject: ReplaySubject<IClaims>;
    public claimStream: Observable<IClaims>;
    public claims: IClaims = {};

    constructor(
        private _userService: UserService,
        @Inject(SETTINGS) private _settings: ISettings,
        private _windowReferenceService: WindowReferenceService
    ) {
        SingletonServiceGuard.register('ClaimService');
        this._application = lowerFirst(_settings.applicationName);
        this._claimSubject = new ReplaySubject<IClaims>(1);
        this.claimStream = this._claimSubject.asObservable();

        this._userService.user$.subscribe((user) => {
            if (!user) {
                this.claims = {};
                this._claimSubject.next(this.claims);
            }

            if (!this._settings.production) {
                this.claims = this._windowReferenceService.getItemFromLocalStorage(
                    LocalStorageKeys.Claims
                ) as IClaims;
                if (this.claims) {
                    this._hasLocalClaims = true;
                }
            }

            if (!this._hasLocalClaims) {
                this.claims = {};
                if (user && user.claims && user.claims[this._application]) {
                    user.claims[this._application].forEach((x: number) => {
                        this.claims[`${x}`] = true;
                    });
                }
            }

            this._claimSubject.next(this.claims);
        });

        this._userService.logout$.subscribe(() => {
            this._hasLocalClaims = false;
            this.claims = {};

            this._claimSubject.next(this.claims);
        });
    }

    public isOn(claim: number): Observable<boolean> {
        return this.claimStream.pipe(map((claims) => !!claims[`${claim}`]));
    }

    public isOnSync(claim: number): boolean {
        return !!this.claims[`${claim}`];
    }

    public updateLocalClaims(claims: { [key: number]: boolean }): void {
        if (this._settings.production) {
            return;
        }

        this.claims = claims;
        this._windowReferenceService.setItemInLocalStorage(LocalStorageKeys.Claims, claims);
        this._hasLocalClaims = true;

        this._claimSubject.next(this.claims);
    }

    public async removeLocalClaims(): Promise<void> {
        this._windowReferenceService.setItemInLocalStorage(LocalStorageKeys.Claims, null);
        this._hasLocalClaims = false;

        const user = await this._userService.user$.pipe(first()).toPromise();

        this.claims = {};
        if (user && user.claims && user.claims[this._application]) {
            user.claims[this._application].forEach((x) => {
                this.claims[x] = true;
            });
        }

        this._claimSubject.next(this.claims);
    }
}
